import React from 'react';
import PropTypes   from 'prop-types';

import * as S                 from './tile-qkn.module.scss';
import { ProductName }        from '../../../common/product/prod-name';
import { TagLine }            from '../../../common/product/tag-line';
import { Expiry }             from '../../../common/product/expiry';
import { setCtxNestedObj }    from '../../../../context/ctx-actions';
import { useGlobalCtx }       from '../../../../context/ctx-hook';
import { getBillManagerType } from '../../../../context/selectors/bill-selectors';
import * as ProductSelectors  from '../../../../context/selectors/product-selectors';
import * as UserSelectors     from '../../../../context/selectors/user-selectors';
import * as StateConstants    from '../../../../helpers/constants/state-constants';
import { useAutoRenew }       from '../../../../helpers/hooks/use-auto-renew';
import * as RouteHelpers      from '../../../../helpers/route-helper';
import { CancelDowngradeBox } from '../../../modals/change-plans/cancel-downgrade';
import { SingleProductRow }   from '../unified-sub-tile/product-single-row';
import RedSquareLogo          from '../../../../assets/imgs/brand/rebrand-square-logo-red.svg';
import {
  getMaxEntitlement,
  getBmTierData
} from '../../../../context/selectors/bill-selectors';
import { TileBm } from '../tile-bm/bm-tile';

const getAccountMsg = ({ active, autoRenew }, hasPayment) => {
  if (!active) {
    return {
      title: 'Expired Subscription',
      message: 'This subscription is no longer active',
    };
  } else if (!hasPayment) {
    return {
      title: 'No Payment Method',
      message: 'In order to not lose your connected services, add a payment method for renewal'
    };
  } else if (!autoRenew) {
    return {
      title: 'Renewal Canceled',
      message: 'Your subscription has been canceled but it is still valid until your expiration date. In order to avoid expiration, you must resume your subscription.'
    };
  } else {
    return false;
  }
};

const TileQkn = ({isModal = false}) => {
  const {globalDispatch, state} = useGlobalCtx();
  const PRODUCT      = `quicken-${state.country}`;
  const BM_TYPE      = getBillManagerType(state);
  const HAS_BM_PAID  = BM_TYPE === 'PAID' ? state.subscriptions?.['billmanager'] : false;
  const BM_SUB       = ProductSelectors.getSubscription(state, 'billmanager');
  const HAS_QOB_ENT  = ProductSelectors.getQobHasEntitlement(state);
  const HAS_PAID_QOB = !!state.subscriptions?.['quicken-online-backup'];
  const HAS_QOB      = HAS_QOB_ENT || HAS_PAID_QOB;
  const QOB_SUB      = ProductSelectors.getSubscription(state, 'quicken-online-backup');
  const SUB          = ProductSelectors.getSubscription(state, PRODUCT);
  const PENDING_DOWNGRADE = SUB?.renewalTierUriName ? SUB?.renewalTierUriName !== SUB?.tierUriName : false;
  const HAS_PAYMENT  = UserSelectors.getProductsPaymentMethod(PRODUCT, state);
  const MESSAGE      = getAccountMsg(SUB, HAS_PAYMENT);
  const ALL_SUBS     = [SUB];
  const AR_ON_SUBS   = [];
  const AR_OFF_SUBS   = [];
  const EXPIRED_SUBS   = [];

  if(!SUB?.active) {
    EXPIRED_SUBS.push(SUB);
  } else if(SUB?.autoRenew) {
    AR_ON_SUBS.push(SUB);
  } else if(!SUB?.autoRenew) {
    AR_OFF_SUBS.push(SUB);
  }

  if(HAS_PAID_QOB) {
    if(!QOB_SUB?.active) {
      // Only show expired QOB sub if they DONT have a QOB entitlement
      if(!HAS_QOB_ENT) {
        EXPIRED_SUBS.push(QOB_SUB);
      }
    } else if(QOB_SUB?.autoRenew) {
      AR_ON_SUBS.push(QOB_SUB);
    } else if(!QOB_SUB?.autoRenew) {
      AR_OFF_SUBS.push(QOB_SUB);
    }

    ALL_SUBS.push(QOB_SUB);
  }


  const ALL_SUBS_EXPIRED = EXPIRED_SUBS.length > 0 && AR_ON_SUBS.length === 0 && AR_OFF_SUBS.length === 0;
  const ALL_EXPIRED_SKUS = EXPIRED_SUBS.map(sub => {
    if(sub?.active === false) {
      const buyNowSku = state.skus[sub?.renewalSku] ? sub?.renewalSku : state.defaultSkus?.[state.country]?.[sub?.tierUriName];
      return buyNowSku;
    }
  });
  const BUY_NOW_SKU_PARAM = ALL_EXPIRED_SKUS.join(',');

  // console.log('ALL_SUBS:', ALL_SUBS);
  // console.log('ALL_SUBS_EXPIRED:', ALL_SUBS_EXPIRED);
  // console.log('AR_ON_SUBS:', AR_ON_SUBS);
  // console.log('AR_OFF_SUBS:', AR_OFF_SUBS);
  // console.log('EXPIRED_SUBS:', EXPIRED_SUBS);
  // console.log('ALL_SUBS_EXPIRED:', ALL_SUBS_EXPIRED);
  // console.log('ALL_EXPIRED_SKUS:', ALL_EXPIRED_SKUS);


  let {active, autoRenew, tierUriName, renewalFrequency, expireOn, renewalSku, effectiveAt} = SUB;
  const handleAROn = useAutoRenew(PRODUCT, true);

  const manageSubscription = React.useCallback(
    async () => globalDispatch(setCtxNestedObj('modal', {
      type:       StateConstants.MODALS.SUBSCRIPTION,
      ctx:        {product: [SUB]},
      isLoading:  true,
      hasError:   false,
      isDisabled: false
    })),
    [globalDispatch, PRODUCT]
  );

  const handleUpdateBilling = React.useCallback(
    () => globalDispatch(setCtxNestedObj('modal', {
      type:       StateConstants.MODALS.UPDATE_PAYMENT,
      ctx:       { product: PRODUCT, paymentOnly: true},
      isLoading:  true,
      hasError:   false,
      isDisabled: false
    })),
    [globalDispatch, PRODUCT]
  );

  const updatePaymentModal = React.useCallback(
    () => globalDispatch(setCtxNestedObj('modal', {
      type:       StateConstants.MODALS.UPDATE_PAYMENT,
      ctx:        { product: PRODUCT, paymentOnly: false, isDefaultPayment: true},
      isLoading:  true,
      hasError:   false,
      isDisabled: false
    })),
    [globalDispatch, PRODUCT]
  );

  const HAS_ADDONS = HAS_QOB || BM_TYPE != 'NONE';

  const BM_MAX_ENT = getMaxEntitlement(state);
  const BM_DATA = getBmTierData(BM_MAX_ENT);
  const QOB_ENT_DATA = HAS_QOB_ENT ? ProductSelectors.getShittyHardcodedQobData(state) : false;

  // console.log('AR_ON_SUBS:', AR_ON_SUBS);
  // console.log('AR_OFF_SUBS:', AR_OFF_SUBS);
  // console.log('ALL_SUBS_EXPIRED:', ALL_SUBS_EXPIRED);

  return (
    <div className={isModal ? S.qknModalWrapper : S.qknWrapper} data-testid="SUB_QKN">
      <div className={`${S.tile} ${SUB.tierUriName}`}>
        {(AR_ON_SUBS.length > 0 || ALL_SUBS_EXPIRED) && (
          <div className={S.subTileContain}>
            <div className={S.subTileLeft}>
              {AR_ON_SUBS.map((sub, i) => {
                return (
                  <div className={S.grpWrapper} key={i}>
                    <div className={S.productRow} >
                      <img className={S.rowLogo} alt="square product logo" src={RedSquareLogo}></img>
                      <div className={S.productName}>
                        <ProductName sub={sub} product={sub.productName} className={S.acmeName} nameStyle='inline'/>
                        <TagLine uriName={sub.tierUriName} className={S.tagLine} />
                      </div>
                    </div>

                    {/* Show BM as an 'addon' entitlement */}
                    {/* This is shit code */}
                    {['quicken-us', 'quicken-ca'].includes(sub.productLineUriName) && BM_TYPE ==='FREE' && BM_DATA &&
                      <div className={S.productRow}>
                        <img className={S.rowLogo} alt="square product logo" src={ProductSelectors.getProductLogo(sub)}></img>
                        <div className={S.productName}>
                          <ProductName sub={BM_DATA} product={BM_DATA.productName} className={S.acmeName} nameStyle='inline'/>
                          <TagLine uriName={BM_DATA.tier} className={S.tagLine} />
                        </div>
                      </div>
                    }

                    {/* Show QOB as an 'addon' entitlement */}
                    {/* This is shit code */}
                    {['quicken-us', 'quicken-ca'].includes(sub.productLineUriName) && HAS_QOB_ENT &&
                      <div className={S.productRow}>
                        <img className={S.rowLogo} alt="square product logo" src={ProductSelectors.getProductLogo(QOB_ENT_DATA)}></img>
                        <div className={S.productName}>
                          <ProductName sub={QOB_ENT_DATA} product={'quicken-online-backup'} className={S.acmeName} nameStyle='inline'/>
                          <TagLine uriName={QOB_ENT_DATA.tierUriName} className={S.tagLine} />
                        </div>
                      </div>
                    }
                  </div>

                );
              })}

              {ALL_SUBS_EXPIRED && (
                EXPIRED_SUBS.map((sub, i) => {
                  return (
                    <div className={S.grpWrapper} key={i}>
                      <div className={S.productRow} >
                        <img className={S.rowLogo} alt="square product logo" src={RedSquareLogo}></img>
                        <div className={S.productName}>
                          <ProductName sub={sub} product={sub.productName} className={S.acmeName} nameStyle='inline'/>
                          <TagLine uriName={sub.tierUriName} className={S.tagLine} />
                        </div>
                      </div>

                      {/* Show BM as an 'addon' entitlement */}
                      {/* This is shit code */}
                      {['quicken-us', 'quicken-ca'].includes(sub.productLineUriName) && BM_TYPE ==='FREE' && BM_DATA &&
                        <div className={S.productRow}>
                          <img className={S.rowLogo} alt="square product logo" src={ProductSelectors.getProductLogo(sub)}></img>
                          <div className={S.productName}>
                            <ProductName sub={BM_DATA} product={BM_DATA.productName} className={S.acmeName} nameStyle='inline'/>
                            <TagLine uriName={BM_DATA.tier} className={S.tagLine} />
                          </div>
                        </div>
                      }

                      {/* Show QOB as an 'addon' entitlement */}
                      {/* This is shit code */}
                      {['quicken-us', 'quicken-ca'].includes(sub.productLineUriName) && HAS_QOB_ENT &&
                        <div className={S.productRow}>
                          <img className={S.rowLogo} alt="square product logo" src={ProductSelectors.getProductLogo(QOB_ENT_DATA)}></img>
                          <div className={S.productName}>
                            <ProductName sub={QOB_ENT_DATA} product={'quicken-online-backup'} className={S.acmeName} nameStyle='inline'/>
                            <TagLine uriName={QOB_ENT_DATA.tierUriName} className={S.tagLine} />
                          </div>
                        </div>
                      }
                    </div>
                  );
                })
              )}

              {PENDING_DOWNGRADE && SUB.autoRenew &&
                <CancelDowngradeBox product={PRODUCT} className={'mainTile'} />
              }

              {!isModal && MESSAGE &&
                <div className={S.productAlert}>
                  <p className={S.messageTitle}>{MESSAGE.title}</p>
                  <p>{MESSAGE.message}</p>
                </div>
              }
            </div>


            <div className={S.subTileRight}>
              <div className={`${S.actionBtnWrapper} ${HAS_ADDONS ? S.hasAddons : false}`} >
                {!isModal && active && autoRenew && HAS_PAYMENT && (
                  <button
                    type="button"
                    data-testid="BTN_QKN_MANAGE_SUB"
                    onClick={manageSubscription}
                    className={S.ctaBtnInvert}
                  >Manage Subscription</button>
                )}

                {active && autoRenew && !HAS_PAYMENT && (
                  <button
                    type="button"
                    data-testid="BTN_QKN_ADD_BILLING"
                    onClick={handleUpdateBilling}
                    className={S.ctaBtnInvert}
                  >Add Billing</button>
                )}

                {active && !autoRenew && (
                  <>
                    <button
                      type="button"
                      data-testid="QKN_RESTART_SUB"
                      onClick={HAS_PAYMENT ? handleAROn : updatePaymentModal}
                      className={S.ctaBtn}
                      disabled={state.isDisabled}
                    >Resume Subscription</button>
                  </>
                )}

                {ALL_SUBS_EXPIRED && (
                  <a
                    data-testid="LINK_BUY_NOW_QKN"
                    className={S.ctaBtn}
                    href={RouteHelpers.getEStoreCartUrlAddProduct(BUY_NOW_SKU_PARAM, state.country)}
                  >Buy Now</a>
                )}
              </div>

              <Expiry sub={SUB} product={SUB.productLineUriName} className={S.productExpiry} />
            </div>
          </div>
        )}

        {/* 'Broken out' subs within the coterm subscription */}
        {AR_OFF_SUBS.map((sub, i) =>
          <SingleProductRow key={i} sub={sub} testId={'QKN'}/>
        )}

        {/* Paid Billmanager is a special snowflake */}
        {(BM_TYPE == 'PAID' ) &&
          <SingleProductRow sub={BM_SUB} testId={'BM'}/>
        }

        {/* 'Broken out' expired subs within the coterm subscription */}
        {!ALL_SUBS_EXPIRED && (
          EXPIRED_SUBS.map((sub, i) =>
            <SingleProductRow key={i} sub={sub} testId={'QKN'}/>
          ))
        }
      </div>
    </div>
  );
};

TileQkn.displayName  = 'TileQkn';
TileQkn.propTypes    = {isModal: PropTypes.bool};
TileQkn.defaultProps = {isModal: false};

export { TileQkn };
